import useGlossaryAbilities from './Glossary';
import useReportAbilities from './Reports';
import {
    useUserManagementAbilities,
    useCompanyManagementAbilities,
    useContentManagementAbilities,
    useProgramManagementAbilities,
    useAbilityUtils,
    useAssessmentResultAbilities,
} from './modules';

const useAppAbilities = () => {
    const { grantSuperAdmin } = useAbilityUtils();

    // Import all modular abilities
    const userManagement = useUserManagementAbilities();
    const companyManagement = useCompanyManagementAbilities();
    const contentManagement = useContentManagementAbilities();
    const programManagement = useProgramManagementAbilities();
    const assessmentResult = useAssessmentResultAbilities();
    const reports = useReportAbilities();
    const glossary = useGlossaryAbilities();

    return {
        // Company Management
        canCreateCompany: grantSuperAdmin(() => false),
        canViewAllCompanies: grantSuperAdmin(() => false),
        canViewOwnCompanyDetails: grantSuperAdmin(companyManagement.canViewOwnCompanyDetails),
        canEditCompany: grantSuperAdmin(companyManagement.canEditCompany),

        // User Management
        canViewAllUsers: grantSuperAdmin(userManagement.canViewAllUsers, true),
        canViewInvitations: grantSuperAdmin(userManagement.canViewInvitations, true),
        canCreateOrEditUser: grantSuperAdmin(userManagement.canCreateOrEditUser, true),
        canViewGroups: grantSuperAdmin(userManagement.canViewGroups, true),
        canCreateGroups: grantSuperAdmin(userManagement.canCreateGroups, true),
        canEditGroups: grantSuperAdmin(userManagement.canEditGroups, true),
        canDeleteGroups: grantSuperAdmin(userManagement.canDeleteGroups, true),
        canManageGroupMembers: grantSuperAdmin(userManagement.canManageGroupMembers, true),
        displayUserManagementMenuItem: grantSuperAdmin(userManagement.displayUserManagementMenuItem, true),

        // Course Management
        canManageCourseTemplates: grantSuperAdmin(() => false), // SuperAdmin only, no company needed
        canViewCourseTemplates: grantSuperAdmin(programManagement.canViewCourseTemplates, false),
        canViewCourseInstances: grantSuperAdmin(programManagement.canViewCourseInstances, true),
        canCreateCourseInstance: grantSuperAdmin(programManagement.canCreateCourseInstance, true),
        canEditCourseInstance: grantSuperAdmin(programManagement.canEditCourseInstance, true),
        canDeleteCourseInstance: grantSuperAdmin(programManagement.canDeleteCourseInstance, true),
        canManageCourseInstancesForCompany: grantSuperAdmin(
            programManagement.checkCanManageCourseInstancesForCompany,
            true
        ),

        // Content Management
        canViewModuleDefinitions: grantSuperAdmin(contentManagement.canViewModuleDefinitions, false),
        canViewAssessments: grantSuperAdmin(contentManagement.canViewAssessments, true),
        canManageAssessments: grantSuperAdmin(contentManagement.canManageAssessments, true),
        canViewTraining: grantSuperAdmin(contentManagement.canViewTraining, true),
        canManageTraining: grantSuperAdmin(contentManagement.canManageTraining, true),
        displayContentManagementMenuItem: grantSuperAdmin(contentManagement.displayContentManagementMenuItem, false),
        // Trainee's Content
        displayTraineeContentMenuItem: contentManagement.displayTraineeContentMenuItem,
        canViewTraineeContentAssessment: contentManagement.canViewTraineeAssessments,

        // Program Management
        canViewPrograms: grantSuperAdmin(programManagement.canViewPrograms, true),
        canCreateProgram: grantSuperAdmin(programManagement.canCreateProgram, true),
        canEditProgram: grantSuperAdmin(programManagement.canEditProgram, true),
        canDeleteProgram: grantSuperAdmin(programManagement.canDeleteProgram, true),
        canViewAssessmentRuns: grantSuperAdmin(programManagement.canViewAssessmentRuns, true),
        canCreateAssessmentRuns: grantSuperAdmin(programManagement.canCreateAssessmentRuns, true),
        canEditAssessmentRuns: grantSuperAdmin(programManagement.canEditAssessmentRuns, true),
        canDeleteAssessmentRuns: grantSuperAdmin(programManagement.canDeleteAssessmentRuns, true),
        displayProgramManagementMenuItem: grantSuperAdmin(programManagement.displayProgramManagementMenuItem, false),
        displayTraineeProgramMenuItem: programManagement.displayTraineeProgramMenuItem,

        // Assessment Results
        canViewAssessmentResults: grantSuperAdmin(assessmentResult.canViewAssessmentResults, true),
        canAddManualAssessmentResult: grantSuperAdmin(assessmentResult.canAddManualAssessmentResult, true),
        canEditOwnAssessmentResults: grantSuperAdmin(assessmentResult.canEditOwnAssessmentResults, true),
        canDeleteOwnAssessmentResults: grantSuperAdmin(assessmentResult.canDeleteOwnAssessmentResults, true),
        canManageRunLinkedAssessmentResults: grantSuperAdmin(
            assessmentResult.canManageRunLinkedAssessmentResults,
            true
        ),
        canManageAssessmentResultsForCompany: grantSuperAdmin(
            assessmentResult.canManageAssessmentResultsForCompany,
            true
        ),
        displayAssessmentResultMenuItem: grantSuperAdmin(assessmentResult.displayAssessmentResultMenuItem, true),

        // Reports
        displayReportMenuItem: grantSuperAdmin(reports.displayReportMenuItem, true),
        canViewReports: grantSuperAdmin(reports.canViewReports, true),
        canEditTrainersNotes: grantSuperAdmin(reports.canEditTrainersNotes, true),
        canViewListTraineeReports: grantSuperAdmin(reports.canViewListTraineeReports, true),

        // Glossary
        displayGlossaryMenuItem: grantSuperAdmin(glossary.displayGlossaryMenuItem, true),
        canViewGlossary: grantSuperAdmin(glossary.canViewGlossary, true),
    };
};

export type AppAbilities = ReturnType<typeof useAppAbilities>;

export default useAppAbilities;
